import React, { useEffect, useState } from 'react';
import { Chat, ChatTag, CustomerSupportStatus } from './types';
import Button from '@/UIlibrary/Button/Button';
import Select from '@/UIlibrary/Select/Select';
import Checkbox from '@/UIlibrary/Checkbox/Checkbox';
import InputField from '@/UIlibrary/InputField/InputField';
import { Typography } from '@/UIlibrary/Typography/Typography';
import { ChevronDown, ChevronUp, Filter, Search } from 'react-feather';
import { useTranslations } from 'use-intl';
import { GRAY_COLORS } from '@/UIlibrary/globalTheme';
import { useChatStore } from './useChat';
import { useSearchParams } from 'react-router-dom';

interface FiltersAndSearchProps {
	searchInput: string;
	setSelectedTag: React.Dispatch<React.SetStateAction<'all' | ChatTag>>;
	setSearchInput: React.Dispatch<React.SetStateAction<string>>;
	RenderCustomerSupportStatusIcon: React.FC<{
		status: Chat['customer_support_status'];
		size?: number;
	}>;
}

const FiltersAndSearch: React.FC<FiltersAndSearchProps> = ({
	searchInput,
	setSelectedTag,
	setSearchInput,
	RenderCustomerSupportStatusIcon
}) => {
	const { setFilters, selectedTag, selectedStatuses, searchTerm } = useChatStore();
	const [searchParams] = useSearchParams();
	const [number, setNumber] = useState<string | null>(null);

	const [showFilters, setShowFilters] = useState(true);
	const [statuses, setStatuses] = useState<string[]>(selectedStatuses);
	const t = useTranslations('Support');
	const tagOptions = [
		{ value: 'all', label: t('allTags') },
		{ value: 'originacion_camionetas', label: t('originacionCamionetas') },
		{ value: 'cobranza', label: t('cobranza') },
		{ value: 'originacion_microcredito', label: t('originacionMicrocredito') },
		{ value: 'continuar_conversacion_credito', label: t('continuarConversacionCredito') },
		{ value: 'soporte', label: t('soporte') },
		{ value: 'onboarding', label: t('onboarding') }
	];

	const handleTagChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		setSelectedTag(e.target.value as ChatTag | 'all');
		setFilters({ tag: e.target.value === 'all' ? '' : e.target.value });
	};

	const handleStatusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		let newSelectedStatuses;

		if (selectedStatuses.includes(e.target.name)) {
			newSelectedStatuses = selectedStatuses.filter((s) => s !== e.target.name);
		} else {
			newSelectedStatuses = [...selectedStatuses, e.target.name];
		}

		setStatuses(newSelectedStatuses);
		setFilters({ statuses: newSelectedStatuses });
	};

	const handleSearchChange = () => {
		setFilters({ search: searchInput });
	};

	const clearSearch = () => {
		const urlsearchparams = new URLSearchParams(window.location.search);
		urlsearchparams.delete('number');
		urlsearchparams.delete('name');
		window.history.pushState({}, '', window.location.pathname);
		setFilters({ search: '' });
		setNumber(null);
		window.location.reload();
	};

	useEffect(() => {
		setNumber(searchParams.get('number'));
	}, [searchParams]);

	useEffect(() => {
		if (number && number !== searchTerm) {
			setFilters({ search: number });
		}
	}, [number, searchTerm, setFilters]);

	return (
		<div className="flex flex-col">
			<div className="p-3">
				<InputField
					onChange={(e) => setSearchInput(e.target.value)}
					placeholder={t('Search')}
					size="small"
					shape="pill"
					maxChars={80}
					focused
					endAction={
						<Search
							size={20}
							color={GRAY_COLORS.gray600}
							onClick={handleSearchChange}
							className="cursor-pointer"
						/>
					}
				/>
				{searchTerm && (
					<div className="flex items-center justify-between mt-2">
						<Typography size="sm" color="subtitle">
							{t('LastSearch')} "{searchTerm}"
						</Typography>
						<Button textOnly label={'Limpiar'} size="small" onClick={clearSearch} />
					</div>
				)}
			</div>
			<div className="flex flex-col">
				<div
					className="flex items-center justify-between gap-2 p-3 cursor-pointer hover:bg-gray-100"
					onClick={() => setShowFilters(!showFilters)}
				>
					<div className="flex flex-row gap-2 items-center">
						<Filter size={16} className=" text-gray-500" />
						<Typography size="sm" color="subtitle">
							{t('filters')}
						</Typography>
					</div>
					{showFilters ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
				</div>
				{showFilters && (
					<div className="flex flex-col gap-2">
						<div className="flex flex-col gap-1 px-5">
							<Typography size="sm" color="subtitle">
								{t('filterByTag')}
							</Typography>
							<Select
								name="tag"
								value={selectedTag}
								onChange={handleTagChange}
								options={tagOptions}
							/>
						</div>
						<div className="flex flex-col gap-1 px-5 mt-3">
							<Typography size="sm" color="subtitle">
								{t('filterByStatus')}
							</Typography>
							<div className="flex justify-around mt-2 mb-4 items-center">
								{Object.keys(CustomerSupportStatus).map((status) => (
									<div className="flex flex-col gap-2" key={status}>
										<RenderCustomerSupportStatusIcon
											status={status as CustomerSupportStatus}
											size={20}
										/>
										<div className="flex">
											<Checkbox
												checked={statuses.some((s) => s === status)}
												onChange={(e) => handleStatusChange(e)}
												name={status}
											/>
										</div>
									</div>
								))}
							</div>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default FiltersAndSearch;
